/**
 * FilterBar — template UI chuẩn cho Filter Area trên list page.
 *
 * Bao gồm:
 *   - <FilterBar>          : container ngang `Filter [buttons]   [rightSlot]`
 *   - <FilterButton>       : nút dropdown toggle popover, highlight khi active
 *   - filterBarBtnSx       : style chung cho button trong filter bar
 *
 * Pairs with useFilterPopover (state) + <FilterPopover>/<SearchFilterContent>.
 */
import { type MouseEvent, type ReactNode } from 'react'
import { Button, Stack, type SxProps, type Theme, Typography } from '@mui/material'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { filterBarBtnSx } from './filterBarStyles'

export interface FilterBarProps {
  /** Các <FilterButton> + custom controls (Columns menu...). */
  children: ReactNode
  /** True khi có ít nhất một filter đang active → render "Clear All". */
  hasActiveFilters?: boolean
  onClearAll?: () => void
  /** Optional — render "Clear All (N)" để chỉ rõ Clear All sẽ reset N filter. */
  activeFilterCount?: number
  /** Slot phải — toolbar action buttons (View / Edit / Print...). */
  rightSlot?: ReactNode
  /** Hiển thị nhãn "Filter" trước các nút. Bỏ khi caller muốn custom label. */
  showLabel?: boolean
  /**
   * Override cho container ngoài — dùng khi FilterBar render TRỰC TIẾP bên trong
   * `.app-dataframe` (card không padding, vd `*ReferenceGrid`) để tránh chữ "Filter"
   * dính sát viền card. Page đặt FilterBar NGOÀI `.app-dataframe` (canonical,
   * `ContractListView`) không cần prop này.
   */
  sx?: SxProps<Theme>
}

export function FilterBar({
  children,
  hasActiveFilters,
  onClearAll,
  activeFilterCount,
  rightSlot,
  showLabel = true,
  sx,
}: FilterBarProps) {
  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      useFlexGap
      justifyContent={{ xs: 'flex-start', sm: 'space-between' }}
      alignItems="center"
      sx={{ mb: 0.5, columnGap: { xs: 0.5, sm: 0 }, rowGap: 0.5, ...sx }}
    >
      <Stack
        direction="row"
        spacing={{ xs: 0.25, sm: 0.375 }}
        flexWrap="wrap"
        useFlexGap
        alignItems="center"
        sx={{ minWidth: 0 }}
      >
        {showLabel && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mr: 0.25, display: { xs: 'none', sm: 'inline' } }}
          >
            Filter
          </Typography>
        )}
        {children}
        {hasActiveFilters && onClearAll && (
          <Button
            size="small"
            variant="text"
            color="secondary"
            onClick={onClearAll}
            sx={filterBarBtnSx}
          >
            Clear All{activeFilterCount && activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
          </Button>
        )}
      </Stack>
      {rightSlot}
    </Stack>
  )
}

export interface FilterButtonProps {
  label: string
  /** True khi filter này đang có giá trị → đổi sang contained/primary. */
  active: boolean
  onClick: (event: MouseEvent<HTMLElement>) => void
  /** Disable khi page chưa load xong (optional). */
  disabled?: boolean
}

export function FilterButton({ label, active, onClick, disabled }: FilterButtonProps) {
  return (
    <Button
      size="small"
      variant={active ? 'contained' : 'outlined'}
      color={active ? 'primary' : 'inherit'}
      endIcon={<ArrowDropDownIcon sx={{ fontSize: { xs: 13, sm: 16 } }} />}
      onClick={(e) => {
        // Capture anchor (đồng bộ trong onClick) rồi blur trigger: tránh
        // warning "aria-hidden on focused element" của MUI v5 — Popover áp
        // aria-hidden lên #root trước khi focus-trap dời focus khỏi nút này.
        onClick(e)
        e.currentTarget.blur()
      }}
      disabled={disabled}
      sx={filterBarBtnSx}
    >
      {label}
    </Button>
  )
}
